import React from 'react';
import { Helmet } from 'react-helmet';

interface StructuredDataProps {
  structuredData?: {
    url?: string;
    name?: string;
    _type?: string;
    _context?: string;
    usesDevice?: {
      name?: string;
      _type?: string;
    };
    description?: string;
    normalRange?: string;
    signDetected?: {
      name?: string;
      _type?: string;
    };
    alternateName?: string;
    usedToDiagnose?: Array<{
      name?: string;
      _type?: string;
    }>;
    mainEntityOfPage?: {
      _id?: string;
      _type?: string;
    };
    relevantSpecialty?: {
      name?: string;
      _type?: string;
    };
    indication?: string[];
    outcome?: string;
    contraindication?: string;
    bodyLocation?: string;
    howPerformed?: string;
    preparationAndAftercare?: string;
    status?: string;
    procedureType?: string;
    signOrSymptom?: string[];
    possibleTreatment?: Array<{
      name?: string;
      _type?: string;
    }>;
    medicalSpecialty?: Array<{
      name?: string;
      _type?: string;
    }>;
    provider?: {
      name?: string;
      _type?: string;
      _id?: string;
    };
  };
  pageType?:
    | 'homepage'
    | 'disease'
    | 'therapy'
    | 'team'
    | 'contact'
    | 'default';
}

const parseStructuredData = (
  structuredData: StructuredDataProps['structuredData'],
  pageType: StructuredDataProps['pageType'] = 'default'
) => {
  if (!structuredData) return null;

  // Convert underscore prefixed fields to @ prefixed fields for JSON-LD
  const convertToJsonLd = (obj: unknown): unknown => {
    if (obj === null || obj === undefined) return undefined;

    if (Array.isArray(obj)) {
      const converted = obj.map(convertToJsonLd).filter(item => item !== undefined);
      return converted.length > 0 ? converted : undefined;
    }

    if (typeof obj === 'object') {
      const converted: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(obj)) {
        const convertedValue = convertToJsonLd(value);
        if (convertedValue !== undefined) {
          const newKey = key.startsWith('_') ? `@${key.slice(1)}` : key;
          converted[newKey] = convertedValue;
        }
      }
      return Object.keys(converted).length > 0 ? converted : undefined;
    }

    return obj;
  };

  return convertToJsonLd(structuredData);
};

const StructuredData: React.FC<StructuredDataProps> = ({
  structuredData,
  pageType = 'default',
}) => {
  if (pageType === 'homepage' && !structuredData) {
    return (
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'MedicalBusiness',
                '@id': 'https://magwise.pl/#medicalbusiness',
                name: 'Centrum Medyczne Magwise',
                address: {
                  '@type': 'PostalAddress',
                  streetAddress: 'ul. Siedmiogrodzka 9',
                  addressLocality: 'Warszawa',
                  postalCode: '01-204',
                  addressRegion: 'mazowieckie',
                  addressCountry: 'PL',
                },
                telephone: '+48662277477',
                email: 'kontakt@magwise.pl',
                url: 'https://magwise.pl/',
                description:
                  'Centrum Medyczne Magwise oferuje kompleksową opiekę neuropsychiatryczną, specjalizując się w nowoczesnych metodach leczenia chorób psychicznych i neurologicznych, w tym neurostymulacji TMS i TPS.',
                aggregateRating: {
                  '@type': 'AggregateRating',
                  ratingValue: '4.8',
                  reviewCount: '250',
                },
                openingHoursSpecification: [
                  {
                    '@type': 'OpeningHoursSpecification',
                    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday'],
                    opens: '09:00',
                    closes: '21:00',
                  },
                  {
                    '@type': 'OpeningHoursSpecification',
                    dayOfWeek: 'Friday',
                    opens: '09:00',
                    closes: '17:00',
                  },
                ],
                knowsAbout: [
                  { '@type': 'MedicalSpecialty', name: 'Psychiatry' },
                  { '@type': 'MedicalSpecialty', name: 'Neurology' },
                  { '@type': 'MedicalSpecialty', name: 'Psychotherapy' },
                  { '@type': 'MedicalSpecialty', name: 'Psychodietetics' },
                ],
                containedInPlace: {
                  '@type': 'Place',
                  geo: {
                    '@type': 'GeoCoordinates',
                    latitude: '52.2320',
                    longitude: '20.9610',
                  },
                },
              },
              {
                '@type': 'Organization',
                name: 'Centrum Medyczne Magwise',
                url: 'https://magwise.pl/',
                sameAs: [
                  'https://www.facebook.com/MagwiseCentrumMedyczne',
                  'https://www.linkedin.com/company/magwisetms',
                  'https://www.znanylekarz.pl/placowki/magwise-centrum-medyczne',
                  'https://www.youtube.com/channel/UClp1bu5MbHayaDDptPFacHA',
                ],
              },
            ],
          })}
        </script>
      </Helmet>
    );
  }

  if (!structuredData) return null;

  const jsonLdData = parseStructuredData(structuredData, pageType);

  if (!jsonLdData) return null;

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLdData)}</script>
    </Helmet>
  );
};

export default StructuredData;
